import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { Logger } from '../../services/logger.service';
import { NavigationEnd, Router } from '@angular/router';
import {
  Subscription,
  filter,
  interval,
  startWith,
  switchMap,
  merge,
} from 'rxjs';
import { NotificationService } from '../../../shared/services/notification.service';
import {
  UserContextEnum,
  UserContextService,
} from '../../services/user-context.service';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
})
export class NavigationComponent implements OnInit, OnDestroy {
  private readonly logger = new Logger(NavigationComponent.name);

  public currentRouteTitle = 'Accueil';
  public navigationRoutes: { title: string; iconClass: string; url: string }[] = [];
  private routerSubscription?: Subscription;
  private notificationSubscription?: Subscription;
  private notificationReadSubscription?: Subscription;
  private userContextSubscription?: Subscription;
  public unreadNotificationCount = 0;
  public isCoach = false;

  get progressBarColor(): string {
    return this.isCoach
      ? 'var(--app-brand-green-500)'
      : 'var(--app-brand-orange-500)';
  }

  constructor(
    private router: Router,
    private notificationService: NotificationService,
    private userContextService: UserContextService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Initialize with current user context
    this.isCoach =
      this.userContextService.getCurrentUserContext() === UserContextEnum.Coach;
    this.updateNavigationRoutes();

    // Set initial route title
    this.updateCurrentRouteTitle(this.router.url);

    // Subscribe to route changes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe({
        next: (event: any) => {
          this.updateCurrentRouteTitle(event.url);
        },
        error: error => {
          this.logger.error('Error in router subscription', error);
        },
      });

    // Subscribe to user context changes
    this.userContextSubscription =
      this.userContextService.userContext$.subscribe({
        next: context => {
          this.isCoach = context === UserContextEnum.Coach;
          this.updateNavigationRoutes();
          this.updateCurrentRouteTitle(this.router.url);
          this.cdr.markForCheck();
        },
        error: error => {
          this.logger.error('Error in user context subscription', error);
        },
      });

    // Créer un observable qui combine le polling régulier et les événements de notification lue
    const notificationUpdates$ = merge(
      interval(30000).pipe(startWith(0)),
      this.notificationService.notificationRead$
    ).pipe(switchMap(() => this.notificationService.getUnreadCount()));

    // S'abonner à l'observable combiné
    this.notificationSubscription = notificationUpdates$.subscribe({
      next: count => {
        this.unreadNotificationCount = count;
      },
      error: error => {
        this.logger.error('Error in notification updates subscription', error);
      },
    });
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.notificationSubscription?.unsubscribe();
    this.userContextSubscription?.unsubscribe();
  }

  private updateCurrentRouteTitle(url: string): void {
    // Find matching route using partial URL match
    const matchingRoute = this.navigationRoutes.find(
      route => url.startsWith(route.url) || route.url.startsWith(url)
    );

    if (matchingRoute) {
      this.currentRouteTitle = matchingRoute.title;
    }
  }

  private updateNavigationRoutes(): void {
    this.navigationRoutes = [
      {
        title: 'Accueil',
        iconClass: 'pi pi-home',
        url: '/home',
      },
      {
        title: 'Notifs.',
        iconClass: 'pi pi-bell',
        url: '/notifications',
      },
      {
        title: this.isCoach ? 'Carte des coachs' : 'Trouver un coach',
        iconClass: 'pi pi-map-marker',
        url: '/map',
      },
      {
        title: 'Messages',
        iconClass: 'pi pi-comment',
        url: '/chat',
      },
      {
        title: 'Profil',
        iconClass: 'pi pi-user',
        url: '/profile',
      },
    ];
  }

  trackByRoute(_index: number, route: { url: string }): string {
    return route.url;
  }

  public getCurrentRouteIndex(): number {
    return this.navigationRoutes.findIndex(
      route => route.title === this.currentRouteTitle
    );
  }

  public getProgressRate(): number {
    return (this.getCurrentRouteIndex() + 1) / this.navigationRoutes.length;
  }

  public getProgressStartAt(): number {
    return this.getCurrentRouteIndex() / this.navigationRoutes.length;
  }
}
